import {
  head,
  pathOr,
  split,
  tail,
  filter,
  equals,
  map,
  cond,
  compose,
  find,
  lensPath,
  set,
  includes,
  flatten,
  when,
  keys,
  uniq,
  not,
  trim
} from 'ramda';
import { capitalize } from 'src/utilities/capitalize';
import { MIN_PAGE_SIZE } from 'src/constants';
import { mkCondition } from './status';
import { filterIsNotEmpty, isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import { formatCurrencyFn } from 'src/utilities/formatCurrency';
import { pruneAddress } from 'src/utilities/properties';
import { API_RESOURCE_BORROWERS, APPLICATION_CREATEDBY, APPLICATION_LAUNCHER_TYPE, KIND_LOANAPP, LABELS, LOAN_CLASS, META_DATA, OBJECT_META, YES_UPPERCASE_Y } from '../constants';
import { getByOwnerReferenceKind } from './build';
import { MORTGAGE, getOriginationType } from 'src/features/constants';
import { currentAppSession, getInbranchType } from 'src/utilities/storage';
import SiteSettings from 'src/siteSetting';
import { CUSTOMIZE } from 'src/components/Widgets/Applications/LoanStages/constants';
import { ILoanSearchParams } from 'src/types/Loan';
import { getNamespaceId } from './namespaces';
import { SORT_BY_APPLICATION_NUMBER_IN_ASC_LOAN_KEY, SORT_BY_APPLICATION_NUMBER_IN_DESC_LOAN_KEY } from 'src/components/sorting';
import { LOAN_APP_SORT_WITH_APPLICATION_NUMBER_KEY } from 'src/utilities/apiMapQueryParams';
import { canGuard } from 'src/features/RBAC/Can';
import {
  ADVISOR_PIPELINE_APPLICATIONS_BRANCH_GROUPING,
  ADVISOR_PIPELINE_APPLICATIONS_BY_BRANCH,
  ADVISOR_PIPELINE_APPLICATIONS_BY_TEAMS
} from 'src/features/RBAC/Rules/permissionData';
import { uniqueId } from 'src/utilities/borrowers';
import { buildLabel } from 'src/utilities/naming';
import { PipeLineFilterByFinancials } from './PipeLineFilterByFinancials';
import { EMAIL_VALIDATION_REGEX } from 'src/utilities/emailValidator';

export const mkMatchAndSearchParams = (searchParams: ILoanSearchParams) => {
  const loanAppParams = new LoanAppSearchParams(searchParams)
  return loanAppParams.getSearchParams();
};


const getCodes = (objs: any[]) => filter(isNotEmpty, map(pathOr('', ['code']), objs))


export const getBranchLabels = (filters: {
  branches: any[],
  authenticatedBranches: any[],
  permissions: any[]
}) => {

  const { permissions, authenticatedBranches, branches } = filters;
  let branchCodes: any = []
  const hasBranchAccess = canGuard(permissions, ADVISOR_PIPELINE_APPLICATIONS_BY_BRANCH)
  const hasBranchGrouping = canGuard(permissions, ADVISOR_PIPELINE_APPLICATIONS_BRANCH_GROUPING)
  if (hasBranchAccess || hasBranchGrouping) {
    branchCodes = flatten([getCodes(authenticatedBranches)])
  }

  if (hasBranchGrouping) {
    branchCodes = flatten([
      branchCodes,
      map((branch: any) => {
        const getLabel = pathOr('', [OBJECT_META, LABELS, buildLabel('branch_grouping')])
        const groupingLabel = getLabel(branch)
        return getCodes(filter(isNotEmpty, map(
          (br: any) => {
            const hasMatch = equals(groupingLabel, getLabel(br)) && !equals(uniqueId(br), uniqueId(branch))
            return hasMatch && br
          },
          branches)))
      }, authenticatedBranches)
    ])
  }

  return map((code: string) => { return { 'lendsmart_sh/branch_code': code } }, branchCodes)
}

export const mkLoanAppLabelParams = (data: {
  priority?: string,
  collateralType?: string,
  initiatedGroup?: string,
  untouched?: string,
  branchFilters?: {
    branches: any[],
    authenticatedBranches: any[],
    permissions: any[]
  }
}) => {
  const { priority, initiatedGroup, untouched, branchFilters, collateralType } = data;
  const allParams = compose(
    when(
      () => isNotEmpty(priority),
      set(lensPath(['loan_priority']), priority)
    ),
    when(
      () => isNotEmpty(collateralType),
      set(lensPath(['collateral_type']), collateralType)
    )
  )({})

  const anyParams = compose(
    when(
      () => isNotEmpty(initiatedGroup),
      set(lensPath(['loan_application_initiate_process_by']), initiatedGroup)
    ),
    when(
      () => equals(YES_UPPERCASE_Y, untouched),
      set(lensPath(['lendsmart_sh/untouched_by_assignee']), untouched)
    )
  )({})

  const anyOf = filter(isNotEmpty, flatten([
    map((k: string) => { return { [k]: anyParams[k] } }, keys(anyParams)),
    isNotEmpty(branchFilters) ? getBranchLabels(branchFilters as any) : []
  ]))

  return compose(
    when(
      () => isNotEmpty(anyOf),
      set(lensPath(['is_any_of']), anyOf)
    ),
    when(
      () => isNotEmpty(allParams),
      set(lensPath(['is_all_of']), allParams)
    )
  )({})
}

const getLoanAssingeeLabels = (
  data: {
    teams: any[],
    permissions: any[]
  }
) => {
  const { permissions, teams } = data;
  let assigneeEmails: any = filterIsNotEmpty(
    uniq(flatten([
      map((team: any) => {
        return map(pathOr('', ['metadata', 'member_email']), pathOr([], ['team_members'], team))
      }, teams)
    ]))
  )
  const hasTeamAccess = canGuard(permissions, ADVISOR_PIPELINE_APPLICATIONS_BY_TEAMS)
  const assigneeLabels = hasTeamAccess ? map((email: string) => {
    return {
      "lendsmart_sh/loanapp_assigned_to_advisor_email": email
    }
  }, assigneeEmails) : []
  return assigneeLabels
}

export const mkLoanStatusMetadataParams = (data: {
  teams: any[],
  permissions: any[]
}) => {
  const allParams = {}
  const anyOf = getLoanAssingeeLabels(data)
  return compose(
    when(
      () => isNotEmpty(anyOf),
      set(lensPath(['is_any_of']), anyOf)
    ),
    when(
      () => isNotEmpty(allParams),
      set(lensPath(['is_all_of']), allParams)
    )
  )({})
}

/////// LOCAL PRIVATE
////
/// Split a search param eg: "name.an,date.asc" first
const splitMainParms = (parms: any) => split(',', parms);
const searchDefaults = [
  { def_for: 'name', def_by: '' },
  { def_for: 'date', def_by: 'asc' },
];
export const searchDefault = (parms_for: any) => {
  return filter((p) => equals(p.def_for, parms_for), searchDefaults);
};
const splitParms = (parms: any) => {
  const p = split('|', parms);
  const name_for = head(p) || '';
  const name_by = head(tail(p)) || searchDefault(name_for);
  return { name_for, name_by };
};

const mapSearchParms = (search: any, key_for: any) => {
  const t = map(splitParms, splitMainParms(search));
  return find((s) => equals(s.name_for, key_for), t);
};

const splitParamsByKey = (parms: any, key: string) =>
  mapSearchParms(parms, key) || '';

export const mkLoanAppContext = (
  accountId: any,
  accountEmail: any,
  signupType: string,
  productType: string,
  specEntities: string[],
  renewal?:string
) => {

  const originConditionType = getOriginationType(signupType)

  const loanStatusMetaData = compose(
    set(
      lensPath
        (
          [META_DATA, APPLICATION_CREATEDBY]
        ), accountEmail),
    //TO-DO:we should move this static resource update to config
    when(
      () => includes(API_RESOURCE_BORROWERS, specEntities),
      set(lensPath([META_DATA, LOAN_CLASS]), "NewLoan")
    ),
    when(
      () => isNotEmpty(getInbranchType()),
      set(lensPath([META_DATA, APPLICATION_LAUNCHER_TYPE]), getInbranchType())
    ),
  )({})


  return {
    account_id: accountId,
    loanapp_labels : {
      "lendsmart_sh/renewal" : capitalize(renewal)
    },
    loan_product_type: productType,
    loan_status: {
      phase: SiteSettings.pipelineUseInitialStatus(),
      conditions: [
        mkCondition(
          `${SiteSettings.pipelineUseInitialStatus()}.Ready`,
          '',
          SiteSettings.pipelineUseInitialStatus(),
          true,
          accountEmail
        ),
        mkCondition(
          `${originConditionType}.Ready`,
          '',
          originConditionType,
          true,
          accountEmail
        ),
      ],
      reason: originConditionType,
      ...loanStatusMetaData
    },
    namespace_id: getNamespaceId(),
  }
}

export const mkMortgageCoLoanInNamespace = (accountId: any) => {
  const mortgageCoLoanApp = {
    context: {
      account_id: accountId,
    },
    resources: ['declarations', 'workflow_intents', 'credit_reports'],
  };
  return mortgageCoLoanApp;
};

export const mkSimpleNotMortgageLoanInNamespace = (
  loanType: string,
  accountId: any,
  accountEmail: any,
  namespace_id: any
) => {
  const notMortgageLoanApp = {
    context: {
      account_id: accountId,
      business_email: accountEmail,
      loan_status: {
        phase: SiteSettings.pipelineUseInitialStatus(),
        conditions: []
      },
      email: accountEmail,
      namespace_id,
    },
    action_type: 'NotMortgage',
    type_of_loan: loanType,
    sub_type_of_loan: loanType,
  };
  return notMortgageLoanApp;
};


export const splitDateOrderParms = (parms: any) =>
  mapSearchParms(parms, 'date') || '';
export const splitNameOrderParms = (parms: any) =>
  mapSearchParms(parms, 'name') || '';
export const getOrderByDateOrNameOrAssignee = (orderBy: any) => {
  return cond([
    [equals('created_at'), () => 'date'],
    [equals('created_at_desc'), () => 'date'],
    [equals('name'), () => 'name'],
    [equals('name_desc'), () => 'name'],
    [equals('assignee'), () => 'assignee'],
    [equals('assignee_desc'), () => 'assignee'],
    [() => includes(orderBy, [SORT_BY_APPLICATION_NUMBER_IN_ASC_LOAN_KEY, SORT_BY_APPLICATION_NUMBER_IN_DESC_LOAN_KEY]), () => LOAN_APP_SORT_WITH_APPLICATION_NUMBER_KEY],
    [equals(SORT_BY_APPLICATION_NUMBER_IN_DESC_LOAN_KEY), () => 'assignee'],
  ])(orderBy);
};
export const getOneLoanAppURL = (thisLoanId: any, thisLoanType: string) => {
  return `/internal/applications/${thisLoanId}/${thisLoanType}?thisurl=Overview`;
};
export const mkSingleLoanContext = () => {
  const { loanAppId, loanType, loanRoleAccountId, teamId } = currentAppSession();
  return {
    loanId: loanAppId,
    teamId,
    loanRoleAccountId,
    thisLoanType: loanType,
  };;
};

/**
 *
 * @param loans
 * @param propertyDetails
 * @returns
 * attach property details inside loanapp when property ownerreference loanapp id equals loanId
 *
 */
export const updPropertyInLoanApp = (loans: any, propertyDetails: any) => {
  return map(
    (loan) => ({
      property: {
        ...find(
          (property: any) =>
            equals(
              pathOr('', ['id'], loan),
              pathOr(
                '',
                ['uid'],
                getByOwnerReferenceKind(KIND_LOANAPP, property)
              )
            ) && property,
          propertyDetails
        ),
      },
      ...loan,
    }),
    loans
  );
};

/**
 *
 * @param loanDetails
 * @returns
 * Mutate loanapp from grpahql response
 */
export const mutateLoanApp = (loanDetails: any) => {
  return map((loan: any) => {
    return {
      loanapp_role: pathOr([], ['loanapp_roles'], loan),
      created_at: pathOr([], ['created_at'], loan),
      loan_type: pathOr('', ['type_of_loan'], loan),
      id: pathOr('', ['id'], loan),
    };
  }, loanDetails);
};


/**
 *
 * @param accountId
 * @param LoanApp
 * @returns
 * get current loanapp details match with logged user's account id
 */
export const getCurrentLoanApp = (accountId: any, LoanApp: any) => {
  const result = map(
    (loan) => ({
      loan_app: filter(
        (item: any) =>
          equals(accountId, pathOr('', ['object_meta', 'account'], item)),
        pathOr([], ['loanapp_role'], loan)
      ),
      ...loan,
    }),
    LoanApp
  );
  return result;
};

/**
 *
 * @param accountId
 * @param resource
 * @param loanApp
 * @returns
 * getting current logged users details based on account id
 */
export const getCurrentResourceDetails = (
  accountId: any,
  resource: any,
  loanApp: any
) => {
  const result = pathOr(
    [],
    [resource],
    find(
      (loan: any) =>
        includes(
          accountId,
          map(
            (item: any) => pathOr('', ['object_meta', 'account'], item),
            pathOr([], [resource], loan)
          )
        ),
      loanApp
    )
  );
  return result;
};
/**
 *
 * @param thisLoan
 * @returns
 * getting loan application name from loanapp business or borrower
 */
export const getLoanName = (thisLoan: any) => {
  return pathOr(
    pathOr('', ['spec', 'business', 0, 'business_name'], thisLoan),
    ['spec', 'borrowers', 0, 'profile', 'name'],
    thisLoan
  );
};
/**
 *
 * @param thisLoan
 * @returns
 * getting loan amount from loanapp business or lending_terms
 */
export const getLoanAmount = (thisLoan: any) => {
  const amount = pathOr(
    pathOr('', ['spec', 'business', 0, 'metadata', 'funding_amount'], thisLoan),
    ['spec', 'lending_terms', 0, 'terms', 'amount'],
    thisLoan
  );
  return amount ? formatCurrencyFn(amount) : '';
};
/**
 *
 * @param thisLoan
 * @returns
 *  getting address from loanapp business or property
 */
export const getLoanAddress = (thisLoan: any) => {
  return pathOr(
    pathOr(
      '',
      ['street'],
      pruneAddress(pathOr('', ['spec', 'business', 0, 'address'], thisLoan))
    ),
    ['spec', 'property', 'address', 'street'],
    thisLoan
  );
};

export const getLoanType = (thisLoan: any) => {
  return pathOr(
    pathOr('', ['type_of_loan'], thisLoan),
    ['spec', 'property', 'object_meta', 'labels', 'property_loan_type'],
    thisLoan
  );
};

export const getLoanStatusPhase = (thisLoan: any) => {
  return pathOr('', ['spec', 'loan_status', 0, 'status', 'phase'],
    thisLoan
  );
};


const isValidApplicationNumber = (name: string) => {
  const regex = /^(?=.*\d)[A-Za-z0-9]+(-[0-9]+)?$/;  
  return regex.test(name);
}

class LoanAppSearchParams extends PipeLineFilterByFinancials {
  searchParams: any;

  constructor(searchParams: any) {
    super();
    this.searchParams = searchParams
  }
  getSearchParams() {
    const {
      order,
      orderBy,
      params,
      currentName,
      matchParams,
      currentFilters,
      currentPage,
      currentStatus,
      currentAssignee,
      loanType,
      allAppsCurrentDateFilterName,
      isShortParams,
      loanStatusMetadata,
    } = this.searchParams;


    const searchName = trim(currentName)

    const { matching } = matchParams;

    const ssn = splitParamsByKey(pathOr('', ['search'], params), 'ssn');
    const { startDate, endDate } = this.getDate()
    const { financialParams, financialStatus } = this.getFinancials()

    const loanappLabels = this.loanLabels();
    const isValidEmail = EMAIL_VALIDATION_REGEX.test(searchName);
    const isApplicationNumber = isValidApplicationNumber(searchName)

    const useConditionDate = equals(CUSTOMIZE, allAppsCurrentDateFilterName)
      && (isNotEmpty(currentStatus) || isNotEmpty(matching));

    const data = compose(
      when(() => isNotEmpty(loanappLabels),
        set(lensPath(['loanapp_labels']), loanappLabels)
      ),
      when(
        () => isNotEmpty(loanStatusMetadata),
        set(lensPath(['loanstatus_metadata']), loanStatusMetadata)
      ),
      when(() => equals(true, isShortParams),
        set(lensPath(['use_condition_date']), useConditionDate),
      ),
      when(() => isValidEmail,
        set(lensPath(['email']), searchName)
      ),
      when(() => isApplicationNumber,
        set(lensPath(['application_number']), searchName)
      )
    )({
      page: currentPage,
      page_size: parseInt(
        pathOr(MIN_PAGE_SIZE.toString(), ['page_size'], params)
      ),
      order_for: orderBy,
      order_by: order,
      name: returnIsNotEmptyOrTrue(isValidEmail || isApplicationNumber, '', searchName),
      product_type: this.getProductType(),
      ssn: pathOr('', ['name_by'], ssn),
      iso: '',
      from_date: startDate,
      to_date: endDate,
      assignee: currentAssignee,
      direct_deal: '',
      renewal: '',
      preference_order: '',
      status: this.getStatus(),
      role: this.getRoles(),
      use_financials: financialStatus,
      financials: financialParams,
    });

    if (currentFilters) {
      const filters = JSON.parse(currentFilters);
      const nameKey = includes(loanType, MORTGAGE) ? 'name' : 'owner_name';
      const filterName = pathOr('', ['owner_name'], filters);
      const email = pathOr('', ['email'], filters);
      const application_number = pathOr('', ['application_number'], filters);
      if (filterName || email || application_number) {
        return compose(
          set(lensPath(['application_number']), application_number),
          set(lensPath(['email']), email),
          set(lensPath([nameKey]), filterName)
        )(data);
      }
    }

    return data;
  }

  private getRoles() {
    const { role } = this.searchParams;
    const role_mathching = pathOr('', ['matching'], role);
    const role_not_mathching = pathOr('', ['not_matching'], role);
    return {
      matching: role_mathching,
      not_matching: role_not_mathching,
    }
  }

  private getDate() {
    const { currentDate } = this.searchParams;
    const startDate = head(split(' - ', currentDate));
    const endDate = head(tail(split(' - ', currentDate)));
    return {
      startDate,
      endDate
    }
  }

  private getStatus() {
    const { currentStatus, matchParams } = this.searchParams;
    const matching = pathOr('', ['matching'], matchParams);
    const not_matching = pathOr('', ['not_matching'], matchParams);
    return {
      matching: isNotEmpty(currentStatus) ? currentStatus : matching,
      not_matching: isNotEmpty(currentStatus) ? '' : not_matching,
    }
  }

  private getProductType() {
    const { isShortParams, currentProduct, products } = this.searchParams;
    const productMatching = pathOr('', ['matching'], products);
    const productNotMatching = pathOr('', ['not_matching'], products);

    const result = equals(true, isShortParams) ? {
      matching: isNotEmpty(currentProduct) ? currentProduct : productMatching,
      not_matching: productNotMatching,
    } : (isNotEmpty(currentProduct) ? currentProduct : '')
    return result;
  }


  private loanLabels() {
    const {
      loanappLabels
    } = this.searchParams;

    const labels =
      returnIsNotEmptyOrTrue(
        loanappLabels,
        loanappLabels
      )

    return labels
  }

  private feedPermittedProducts(podRules: any[]) {

    if (!isNotEmpty(podRules)) {
      return podRules
    }

    const financialProducts = map(pathOr('', ['product_type']), podRules)

    const matchingProductParams = this.getProductType();
    const { matching } = matchingProductParams;
    const matchProducts = filter(isNotEmpty, split(',', matching))
    const missingProducts = filter((product: string) => {
      return !includes(product, financialProducts)
    }, matchProducts)

    return flatten(
      [
        podRules,
        map((product: string) => {
          return {
            'product_type': product,
            'loan_amount': {
              min: '0',
              max: '1000000000000'
            }
          }
        }, missingProducts)
      ]
    )

  }
  //Disabling limit check based on the flag.
  private getFinancials() {
    const { podConfigs = [], shouldDisableLimit = false } = this.searchParams;
    const podRules = this.getPodConfigSearchParams(podConfigs)
    const financialParams = {
      matching: not(shouldDisableLimit) ? this.feedPermittedProducts(podRules) : [],
      not_matching: []
    }
    const financialStatus = isNotEmpty(podRules) && not(shouldDisableLimit)
    return {
      financialParams,
      financialStatus
    }
  }
}